import {
  IconLayoutSidebarLeftCollapse,
  IconLayoutSidebarLeftExpand,
  IconLogout,
  IconMenu2,
  IconX,
} from '@tabler/icons-react'
import { useEffect, useRef, useState } from 'react'
import { NavLink, Outlet } from 'react-router'

import { useSession } from '../auth/useSession'
import { Alert, Badge } from '../components/States'
import { describeError } from '../lib/describeError'
import { useMeta } from '../lib/hooks'
import { navItemsForRole } from '../lib/nav'
import { environmentLabel, environmentTone, roleLabel } from '../lib/status'
import { ThemeSwitch } from './ThemeSwitch'
import { useTheme } from './useTheme'

/**
 * Kerangka konsol: sidebar yang bisa dilipat di desktop dan menjadi menu di
 * ponsel, kepala halaman dengan penanda lingkungan nyata, pemilih tema, dan
 * identitas pengguna. Setiap butir menu punya halaman nyata di router.
 */
export function AppShell() {
  const { user, role, logout } = useSession()
  const { choice, resolved, select } = useTheme()
  const meta = useMeta()
  const [collapsed, setCollapsed] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const sidebarRef = useRef<HTMLElement>(null)
  const bodyRef = useRef<HTMLDivElement>(null)
  const [logoutError, setLogoutError] = useState<unknown>(null)
  const [leaving, setLeaving] = useState(false)

  useEffect(() => {
    if (!menuOpen) {
      return
    }
    const previouslyFocused = document.activeElement as HTMLElement | null
    const sidebar = sidebarRef.current
    const body = bodyRef.current
    if (body) body.inert = true
    const overflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    sidebar?.querySelector<HTMLElement>('button, a[href]')?.focus()
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') { event.preventDefault(); setMenuOpen(false) }
      if (event.key === 'Tab') {
        const items = Array.from(sidebar?.querySelectorAll<HTMLElement>('button, a[href]') ?? [])
        const first = items[0]
        const last = items[items.length - 1]
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus() }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus() }
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      if (body) body.inert = false
      document.body.style.overflow = overflow
      if (previouslyFocused?.isConnected) previouslyFocused.focus()
    }
  }, [menuOpen])

  const items = navItemsForRole(role, user?.merchant_scope_restricted)

  return (
    <div className={collapsed ? 'app app--collapsed' : 'app'}>
      <aside
        ref={sidebarRef}
        role={menuOpen ? 'dialog' : undefined}
        aria-modal={menuOpen || undefined}
        className={menuOpen ? 'sidebar sidebar--open' : 'sidebar'}
        id="menu-konsol"
        aria-label="Menu konsol"
      >
        <div className="sidebar__inner">
          <button type="button" className="btn mobile-only" onClick={() => setMenuOpen(false)}>Sembunyikan menu ponsel</button>
          <div className="sidebar__brand">
            <span className="sidebar__logo-slot" aria-hidden="true">
              TP
            </span>
            <span className="sidebar__brand-text">
              <span className="sidebar__wordmark">TAHU Pay</span>
              <span className="sidebar__owner">Pemerintah Kabupaten Sumedang</span>
            </span>
          </div>

          <nav className="sidebar__nav">
            {items.map(({ to, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                end={to === '/'}
                className="sidebar__link"
                title={collapsed ? label : undefined}
                onClick={() => setMenuOpen(false)}
              >
                <Icon size={20} stroke={1.75} aria-hidden="true" />
                <span className="sidebar__link-label">{label}</span>
              </NavLink>
            ))}
          </nav>

          <div className="sidebar__foot">
            TAHU Pay {meta.data ? meta.data.version : ''}
            <br />
            Konsol operasional pembayaran daerah.
          </div>
        </div>
      </aside>

      <div
        className={menuOpen ? 'sidebar-scrim sidebar-scrim--open' : 'sidebar-scrim'}
        onClick={() => setMenuOpen(false)}
        aria-hidden="true"
      />

      <div className="app__body" ref={bodyRef}>
        <header className="topbar">
          <button
            type="button"
            className="btn btn--icon mobile-only"
            aria-expanded={menuOpen}
            aria-controls="menu-konsol"
            onClick={() => setMenuOpen((open) => !open)}
          >
            {menuOpen ? (
              <IconX size={20} stroke={1.75} aria-hidden="true" />
            ) : (
              <IconMenu2 size={20} stroke={1.75} aria-hidden="true" />
            )}
            <span className="visually-hidden">
              {menuOpen ? 'Tutup menu konsol' : 'Buka menu konsol'}
            </span>
          </button>

          <button
            type="button"
            className="btn btn--icon desktop-only"
            aria-pressed={collapsed}
            onClick={() => setCollapsed((value) => !value)}
          >
            {collapsed ? (
              <IconLayoutSidebarLeftExpand size={20} stroke={1.75} aria-hidden="true" />
            ) : (
              <IconLayoutSidebarLeftCollapse size={20} stroke={1.75} aria-hidden="true" />
            )}
            <span className="visually-hidden">
              {collapsed ? 'Lebarkan sidebar' : 'Lipat sidebar'}
            </span>
          </button>

          <span className="topbar__spacer" />

          {meta.data ? (
            <Badge tone={environmentTone(meta.data.environment)}>
              {environmentLabel(meta.data.environment)}
            </Badge>
          ) : meta.isError ? (
            <span className="caption">Lingkungan tidak terbaca</span>
          ) : (
            <span className="caption">Membaca lingkungan...</span>
          )}

          <ThemeSwitch choice={choice} resolved={resolved} onChange={select} />

          <div className="user-menu">
            <span className="user-menu__text">
              <span className="user-menu__name">{user?.name ?? 'Tanpa nama'}</span>
              <span className="user-menu__role">{user ? roleLabel(user.role) : ''}</span>
            </span>
            <button type="button" className="btn btn--sm" disabled={leaving} onClick={() => { setLeaving(true); setLogoutError(null); void logout().catch(setLogoutError).finally(() => setLeaving(false)) }}>
              <IconLogout size={18} stroke={1.75} aria-hidden="true" />
              {leaving ? 'Keluar...' : 'Keluar'}
            </button>
          </div>
        </header>

        <main className="content">
          {logoutError ? <Alert tone="danger">Keluar gagal: {describeError(logoutError)}. Coba Keluar lagi.</Alert> : null}
          <Outlet />
        </main>
      </div>
    </div>
  )
}
